import { isNumber } from './tapi';

export class Point {
  constructor(x = null, y = null) {
    this.set(x, y);
  }

  clone() {
    return new Point(this.x, this.y);
  }

  set(x, y) {
    this.x = isNumber(x) ? Math.trunc(x) : 0;
    this.y = isNumber(y) ? Math.trunc(y) : 0;
  }

  /**
   * @param point {Point}
   */
  assign(point) {
    this.x = point.x;
    this.y = point.y;
  }

  shift(xOffset, yOffset) {
    this.x += xOffset;
    this.y += yOffset;
  }

  /**
   * diagonal proximity to the other point
   * @param point {Point}
   * @returns {number}
   */
  proximity(point) {
    return Math.sqrt(
      Math.pow(point.x - this.x, 2) + Math.pow(point.y - this.y, 2)
    );
  }

  isValid() {
    return isNumber(this.x) && isNumber(this.y);
  }

  toString() {
    return JSON.stringify(this);
  }
}

export class Axis extends Point {
  constructor(el, scrollableParent, width) {
    super(el.offsetLeft, el.offsetTop);

    this.width = el.clientWidth;
    this.height = el.clientHeight;
    this.el = el;
    this.scrollableParent = scrollableParent;
  }

  isInside(externalPoint) {
    return externalPoint.x >= this.x && externalPoint.y >= this.y;
  }

  toInternal(externalPoint) {
    // {100, 405} -> {200, 20}
    externalPoint.shift(
      -(this.x - this.scrollableParent.scrollLeft), // this.el has no scrollLeft
      -(this.y - this.scrollableParent.scrollTop - this.el.scrollTop)
    );
  }

  toExternal(internalPoint) {
    internalPoint.shift(
      this.x - this.scrollableParent.scrollLeft, // this.el has no scrollLeft
      this.y - this.scrollableParent.scrollTop - this.el.scrollTop
    );
  }
}

export class TimePoint extends Point {
  constructor(x, y, time = null) {
    super(x, y);
    this.time = isNumber(time) ? time : 0;
  }

  clone() {
    return new TimePoint(this.x, this.y, this.time);
  }

  set(x, y, time) {
    this.x = isNumber(x) ? Math.trunc(x) : 0;
    this.y = isNumber(y) ? Math.trunc(y) : 0;
    this.time = isNumber(time) ? time : 0;
  }

  isValid() {
    return isNumber(this.x) && isNumber(this.y) && isNumber(this.time);
  }

  toString() {
    return JSON.stringify(this);
  }
}

export class Rectangle {
  constructor(x, y, width, height, setPoints = true) {
    if (setPoints) {
      this.p1 = new Point(x, y);
      this.p2 = new Point(x + width, y + height);
      this.width = this.p2.x - this.p1.x;
      this.height = this.p2.y - this.p1.y;
    }
  }

  toString() {
    return JSON.stringify({ p1: this.p1.toString(), p2: this.p2.toString() });
  }

  getCenterPoint() {
    return new Point((this.p1.x + this.p2.x) / 2, (this.p1.y + this.p2.y) / 2);
  }

  /**
   * check if point is inside
   * @param point {Point}
   * @param [inclusive] {boolean} including right edge point itself
   * @return {boolean}
   */
  includes(point, inclusive = true) {
    if (inclusive) {
      return (
        this.includesHorizontally(point, true) && this.includesVertically(point)
      );
    } else {
      return (
        this.includesHorizontally(point, false) &&
        this.includesVertically(point)
      );
    }
  }

  includesHorizontally(point, inclusive = true) {
    if (inclusive) {
      return point.x >= this.p1.x && point.x <= this.p2.x;
    } else {
      return point.x >= this.p1.x && point.x < this.p2.x;
    }
  }

  includesVertically(point) {
    return point.y >= this.p1.y && point.y < this.p2.y;
  }

  atLeft(point) {
    return this.p1.x > point.x;
  }

  atRight(point) {
    return this.p2.x < point.x;
  }
}

export class TimeRectangle extends Rectangle {
  constructor(x, y, width, height, startTime, endTime) {
    super(x, y, width, height, false);

    this.p1 = new TimePoint(x, y, startTime);
    this.p2 = new TimePoint(x + width, y + height, endTime);
    this.width = this.p2.x - this.p1.x;
    this.height = this.p2.y - this.p1.y;

    this.startTime = startTime;
    this.endTime = endTime;
    this.continuum = (endTime - startTime) / width;

    this.isLongest = false;
  }

  toString() {
    return JSON.stringify({
      p1: this.p1,
      p2: this.p2,
      width: this.width,
      height: this.height,
      continuum: this.continuum,
    });
  }

  isValid() {
    return this.p1.isValid() && this.p2.isValid() && isNumber(this.continuum);
  }
}

export class ImageSpriteStyle {
  constructor(width, height, url) {
    this.styleObj = {
      width: `${width}px`,
      height: `${height}px`,
      backgroundImage: `url('${url}')`,
    };
  }
}

export class ColourCardSpriteStyle {
  constructor(width, height, colourCardCss) {
    this.styleObj = Object.assign(
      {
        width: `${width}px`,
        height: `${height}px`,
      },
      colourCardCss
    );
  }
}

export class SpriteStyle {
  constructor(width, height, url, shift) {
    this.styleObj = {
      width: `${width}px`,
      height: `${height}px`,
      backgroundImage: `url('${url}')`,
      backgroundPosition: `${Math.round(-shift)}px 0`,
    };
  }
}
